import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Loader2, Download, FileText, Users, DollarSign, AlertTriangle,
  ChevronDown, ChevronRight, Send, ShieldCheck, Eye, FileCheck, Building2, ScrollText,
} from 'lucide-react';
import { toast } from 'sonner';

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const W9_TONE = {
  needed: 'bg-amber-100 text-amber-800', received: 'bg-blue-100 text-blue-800',
  under_review: 'bg-blue-100 text-blue-800', validated: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
};

export default function TaxAdmin() {
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [tab, setTab] = useState('reconciliation');
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('admin');
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});
  const isSuper = role === 'super_admin';

  const load = useCallback(async (y, test) => {
    setLoading(true);
    try {
      const r = await api.get(`/tax-center/admin/overview?year=${y}&include_test=${test}`);
      setData(r.data);
    } catch { toast.error('Could not load tax administration'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    api.get('/auth/me').then((r) => setRole(r.data.role)).catch(() => {});
  }, []);
  useEffect(() => { load(year, includeTest); }, [year, includeTest, load]);

  const totals = data?.totals || {};
  const years = data?.available_years || [year];
  const provider = data?.provider || {};

  const toggleRow = async (tid) => {
    const willOpen = !expanded[tid];
    setExpanded((e) => ({ ...e, [tid]: willOpen }));
    if (willOpen) {
      await fetchDetails(tid);
    }
  };

  const fetchDetails = async (tid) => {
    try {
      const r = await api.get(`/tax-center/admin/contractor/${tid}?year=${year}&include_test=${includeTest}`);
      setDetails((d) => ({ ...d, [tid]: r.data }));
    } catch { /* noop */ }
  };

  const [generating, setGenerating] = useState(false);
  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post('/tax-center/admin/forms/generate', { tax_year: year, include_test: includeTest });
      toast.success(`Generated ${r.data.created} form(s); ${r.data.skipped_existing} already existed`);
      load(year, includeTest);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const reclassify = async (payoutId, bucket, tid) => {
    try {
      await api.post('/tax-center/admin/payments/classify', { payout_id: payoutId, bucket });
      toast.success('Payment reclassified');
      await fetchDetails(tid);
      load(year, includeTest);
    } catch { toast.error('Could not reclassify'); }
  };

  const reviewW9 = async (tid, status) => {
    const notes = status === 'rejected' ? (window.prompt('Reason for rejection (shown to contractor):') || '') : '';
    try {
      await api.post(`/tax-center/admin/w9/${tid}/review`, { status, notes });
      toast.success(status === 'validated' ? 'W-9 validated' : 'W-9 marked for correction');
      await fetchDetails(tid);
      load(year, includeTest);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update W-9'); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tax-admin-page">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="taxadmin-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Tax Administration</h1>
              <p className="text-sm text-slate-500">1099-NEC reconciliation, forms &amp; IRS filing</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Switch checked={includeTest} onCheckedChange={setIncludeTest} data-testid="include-test-switch" /> Test data
            </label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28" data-testid="taxadmin-year"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {/* Provider status */}
        <Card className={provider.live_efile ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}>
          <CardContent className="p-3 flex items-start gap-3 text-sm">
            {provider.live_efile ? <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />}
            <div>
              <p className="font-medium text-slate-900" data-testid="provider-status">
                Filing: {provider.live_efile ? 'Electronic e-file active' : 'Manual export mode'}
              </p>
              <p className="text-slate-600">{provider.message}</p>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat icon={DollarSign} label={`${year} 1099-NEC`} value={money(totals.nec_reportable)} tone="text-emerald-600" testid="stat-nec" />
          <Stat icon={Users} label="Contractors" value={totals.contractors || 0} testid="stat-contractors" />
          <Stat icon={FileCheck} label="Require 1099" value={totals.requiring_1099 || 0} testid="stat-require" />
          <Stat icon={AlertTriangle} label="Missing W-9" value={totals.missing_w9 || 0} tone="text-amber-600" testid="stat-missing" />
          <Stat icon={FileText} label="Forms generated" value={totals.forms_generated || 0} testid="stat-forms" />
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="reconciliation" data-testid="tab-recon">Reconciliation</TabsTrigger>
              <TabsTrigger value="forms" data-testid="tab-forms">Forms &amp; Filing</TabsTrigger>
              <TabsTrigger value="corrections" data-testid="tab-corr">Corrections</TabsTrigger>
              <TabsTrigger value="payer" data-testid="tab-payer">Payer</TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
            </TabsList>

            {/* RECONCILIATION */}
            <TabsContent value="reconciliation" className="space-y-3">
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-500">Income counted by actual payment date. Legacy ACH &amp; Stripe included.</p>
                <Button size="sm" onClick={generate} disabled={generating} data-testid="generate-forms-btn">
                  {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} Generate {year} forms
                </Button>
              </div>
              <Card><CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-slate-500 border-b">
                    <th className="py-2 px-3">Contractor</th><th className="px-3 text-right">1099-NEC</th>
                    <th className="px-3">W-9</th><th className="px-3">Requires</th><th className="px-3">Form</th><th className="px-3"></th>
                  </tr></thead>
                  <tbody>
                    {(data?.contractors || []).map((c) => (
                      <React.Fragment key={c.talent_id}>
                        <tr className="border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => toggleRow(c.talent_id)} data-testid={`contractor-row-${c.talent_id}`}>
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-1">
                              {expanded[c.talent_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              <span className="font-medium">{c.name}</span>
                              {c.missing_tax_info && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                            </div>
                          </td>
                          <td className="px-3 text-right font-medium">{money(c.nec_reportable)}</td>
                          <td className="px-3"><Badge className={`text-xs ${W9_TONE[c.w9_status] || ''}`}>{c.w9_status}</Badge></td>
                          <td className="px-3">{c.requires_1099 ? <Badge className="bg-emerald-100 text-emerald-800 text-xs">Yes</Badge> : <span className="text-slate-400">No</span>}</td>
                          <td className="px-3">{c.form_status ? <Badge variant="outline" className="text-xs capitalize">{c.form_status.replace(/_/g, ' ')}</Badge> : <span className="text-slate-400">—</span>}</td>
                          <td className="px-3"></td>
                        </tr>
                        {expanded[c.talent_id] && (
                          <tr><td colSpan={6} className="bg-slate-50 px-3 py-2">
                            {!details[c.talent_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                              <div className="space-y-1">
                                {(details[c.talent_id].payments || []).map((p) => (
                                  <div key={p.payout_id} className="flex items-center justify-between text-xs border-b py-1" data-testid={`admin-payment-${p.payout_id}`}>
                                    <span>{fmtDate(p.paid_at)} · {p.event_name} · {p.method}</span>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">{money(p.gross)}</span>
                                      <Select value={p.bucket} onValueChange={(v) => reclassify(p.payout_id, v, c.talent_id)}>
                                        <SelectTrigger className="h-7 w-28 text-xs" data-testid={`classify-${p.payout_id}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="nec">1099-NEC</SelectItem>
                                          <SelectItem value="k">1099-K</SelectItem>
                                          <SelectItem value="excluded">Exclude</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                ))}
                                <div className="flex items-center justify-between pt-1">
                                  <p className="text-xs text-slate-500">W-9: {details[c.talent_id].tax_profile?.tin_masked || 'not provided'} · <span className="capitalize">{details[c.talent_id].tax_profile?.status}</span></p>
                                  {details[c.talent_id].tax_profile?.has_w9 && details[c.talent_id].tax_profile?.status !== 'validated' && (
                                    <div className="flex gap-1.5">
                                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); reviewW9(c.talent_id, 'validated'); }} data-testid={`w9-validate-${c.talent_id}`}>Validate W-9</Button>
                                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); reviewW9(c.talent_id, 'rejected'); }} data-testid={`w9-reject-${c.talent_id}`}>Needs correction</Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </td></tr>
                        )}
                      </React.Fragment>
                    ))}
                    {(data?.contractors || []).length === 0 && (
                      <tr><td colSpan={6} className="text-center text-slate-500 py-8">No paid contractors for {year}.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent></Card>
            </TabsContent>

            {/* FORMS */}
            <TabsContent value="forms">
              <FormsTab year={year} isSuper={isSuper} onChange={() => load(year, includeTest)} />
            </TabsContent>

            {/* CORRECTIONS */}
            <TabsContent value="corrections"><CorrectionsTab /></TabsContent>

            {/* PAYER */}
            <TabsContent value="payer"><PayerTab isSuper={isSuper} /></TabsContent>

            {/* AUDIT */}
            <TabsContent value="audit"><AuditTab /></TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, tone = 'text-slate-900', testid }) => (
  <Card><CardContent className="p-3">
    <Icon className="w-4 h-4 text-slate-400 mb-1" />
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`text-lg font-bold ${tone}`} data-testid={testid}>{value}</p>
  </CardContent></Card>
);

function FormsTab({ year, isSuper, onChange }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filing, setFiling] = useState(null);
  const [correct, setCorrect] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/tax-center/admin/forms?year=${year}`); setForms(r.data.forms || []); }
    catch { toast.error('Could not load forms'); } finally { setLoading(false); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  const preview = async (f) => {
    try {
      const r = await api.get(`/tax-center/admin/forms/${f.form_id}/preview`, { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
    } catch { toast.error('No preview available'); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <Card><CardContent className="p-3 space-y-2">
      {forms.length === 0 ? <p className="text-sm text-slate-500 py-8 text-center">No forms generated for {year}. Use “Generate forms” on the Reconciliation tab.</p> :
        forms.map((f) => (
          <div key={f.form_id} className="border rounded-lg p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`admin-form-${f.form_id}`}>
            <div className="text-sm">
              <p className="font-medium text-slate-900">{f.talent_email} {f.is_correction && <Badge className="ml-1 text-xs">Corrected</Badge>}</p>
              <p className="text-xs text-slate-500">Box 1 {money(f.box1)} · <span className="capitalize">{f.status.replace(/_/g, ' ')}</span> · IRS: {f.filing_status_label}{f.filing_receipt_id ? ` (${f.filing_receipt_id})` : ''}</p>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => preview(f)} data-testid={`preview-${f.form_id}`}><Eye className="w-4 h-4" /></Button>
              {f.status === 'prepared' && (
                isSuper
                  ? <Button size="sm" onClick={() => act(() => api.post(`/tax-center/admin/forms/${f.form_id}/approve`), 'Form approved & available to contractor')} data-testid={`approve-${f.form_id}`}><ShieldCheck className="w-4 h-4 mr-1" />Approve</Button>
                  : <Badge variant="outline" className="text-xs">Awaiting Super Admin approval</Badge>
              )}
              {['form_available', 'delivered', 'corrected'].includes(f.status) && f.filing_status === 'not_filed' && isSuper && (
                <Button size="sm" onClick={() => act(() => api.post(`/tax-center/admin/forms/${f.form_id}/transmit`), 'Submitted for IRS filing')} data-testid={`transmit-${f.form_id}`}><Send className="w-4 h-4 mr-1" />File with IRS</Button>
              )}
              {['form_available', 'delivered', 'corrected'].includes(f.status) && isSuper && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setFiling(f)} data-testid={`filing-${f.form_id}`}>Record IRS status</Button>
                  <Button size="sm" variant="outline" onClick={() => setCorrect(f)} data-testid={`correct-${f.form_id}`}>Correct</Button>
                </>
              )}
            </div>
          </div>
        ))}

      {/* Record filing status dialog */}
      <Dialog open={!!filing} onOpenChange={(v) => !v && setFiling(null)}>
        <DialogContent data-testid="filing-dialog">
          <DialogHeader><DialogTitle>Record IRS filing acknowledgment</DialogTitle></DialogHeader>
          <FilingForm form={filing} onDone={() => { setFiling(null); load(); }} />
        </DialogContent>
      </Dialog>

      {/* Correct dialog */}
      <Dialog open={!!correct} onOpenChange={(v) => !v && setCorrect(null)}>
        <DialogContent data-testid="correct-dialog">
          <DialogHeader><DialogTitle>Issue a corrected 1099-NEC</DialogTitle></DialogHeader>
          <CorrectForm form={correct} onDone={() => { setCorrect(null); load(); onChange?.(); }} />
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
}

function FilingForm({ form, onDone }) {
  const [status, setStatus] = useState('accepted');
  const [receipt, setReceipt] = useState('');
  const [errors, setErrors] = useState('');
  const submit = async () => {
    try {
      await api.post(`/tax-center/admin/forms/${form.form_id}/filing-status`, { filing_status: status, filing_receipt_id: receipt || null, errors: errors || null });
      toast.success('Filing status recorded'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-3">
      <div className="space-y-1"><Label className="text-xs">IRS result</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="filing-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="accepted_with_errors">Accepted with errors</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1"><Label className="text-xs">IRS receipt / confirmation ID</Label><Input value={receipt} onChange={(e) => setReceipt(e.target.value)} data-testid="filing-receipt" /></div>
      <div className="space-y-1"><Label className="text-xs">Errors (if rejected)</Label><Textarea value={errors} onChange={(e) => setErrors(e.target.value)} /></div>
      <DialogFooter><Button onClick={submit} data-testid="filing-save">Save</Button></DialogFooter>
    </div>
  );
}

function CorrectForm({ form, onDone }) {
  const [box1, setBox1] = useState(form?.box1 || 0);
  const [box4, setBox4] = useState(form?.box4 || 0);
  const [reason, setReason] = useState('');
  const submit = async () => {
    try {
      await api.post(`/tax-center/admin/forms/${form.form_id}/correct`, { box1_nonemployee_comp: Number(box1), box4_federal_withheld: Number(box4), reason });
      toast.success('Corrected form issued'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Box 1 — Nonemployee comp</Label><Input type="number" value={box1} onChange={(e) => setBox1(e.target.value)} data-testid="correct-box1" /></div>
        <div className="space-y-1"><Label className="text-xs">Box 4 — Fed tax withheld</Label><Input type="number" value={box4} onChange={(e) => setBox4(e.target.value)} data-testid="correct-box4" /></div>
      </div>
      <div className="space-y-1"><Label className="text-xs">Reason</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} /></div>
      <DialogFooter><Button onClick={submit} data-testid="correct-save">Issue correction</Button></DialogFooter>
    </div>
  );
}

function CorrectionsTab() {
  const [list, setList] = useState([]);
  const load = useCallback(async () => {
    try { const r = await api.get('/tax-center/admin/corrections'); setList(r.data.corrections || []); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const resolve = async (id, status) => {
    const notes = window.prompt('Notes for the contractor (optional):') || '';
    try { await api.post(`/tax-center/admin/corrections/${id}/resolve`, { status, admin_notes: notes }); toast.success('Updated'); load(); }
    catch { toast.error('Failed'); }
  };
  return (
    <Card><CardContent className="p-3 space-y-2">
      {list.length === 0 ? <p className="text-sm text-slate-500 py-8 text-center">No correction requests.</p> :
        list.map((c) => (
          <div key={c.id} className="border rounded-lg p-3 text-sm" data-testid={`admin-correction-${c.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium">{c.talent_email} · <span className="capitalize">{c.category}</span> · {c.tax_year}</span>
              <Badge variant="outline" className="capitalize">{c.status.replace(/_/g, ' ')}</Badge>
            </div>
            <p className="text-slate-600 mt-1">{c.description}</p>
            {['requested', 'under_review'].includes(c.status) && (
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => resolve(c.id, 'under_review')}>Mark reviewing</Button>
                <Button size="sm" onClick={() => resolve(c.id, 'approved')} data-testid={`resolve-approve-${c.id}`}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => resolve(c.id, 'rejected')}>Reject</Button>
              </div>
            )}
          </div>
        ))}
    </CardContent></Card>
  );
}

function PayerTab({ isSuper }) {
  const [p, setP] = useState(null);
  const [f, setF] = useState({ name: '', legal_name: '', ein: '', address1: '', city: '', state: '', zip_code: '', phone: '' });
  const load = useCallback(async () => {
    try { const r = await api.get('/tax-center/admin/payers'); const d = (r.data.payers || [])[0]; setP(d); if (d) setF((x) => ({ ...x, name: d.name || '', legal_name: d.legal_name || '', address1: d.address1 || '', city: d.city || '', state: d.state || '', zip_code: d.zip_code || '', phone: d.phone || '' })); }
    catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api.post('/tax-center/admin/payers', f); toast.success('Payer saved'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Card><CardContent className="p-4 space-y-3" data-testid="payer-tab">
      <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-500" /><h3 className="font-semibold">Payer entity (sole legal payer)</h3></div>
      {p?.ein_masked && <p className="text-sm text-emerald-700">EIN on file: {p.ein_masked}</p>}
      <div className="grid md:grid-cols-2 gap-3">
        <F label="Payer name"><Input value={f.name} onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))} data-testid="payer-name" /></F>
        <F label="Legal name"><Input value={f.legal_name} onChange={(e) => setF((x) => ({ ...x, legal_name: e.target.value }))} /></F>
        <F label="EIN (re-enter to update)"><Input type="password" value={f.ein} onChange={(e) => setF((x) => ({ ...x, ein: e.target.value }))} data-testid="payer-ein" /></F>
        <F label="Phone"><Input value={f.phone} onChange={(e) => setF((x) => ({ ...x, phone: e.target.value }))} /></F>
        <F label="Address"><Input value={f.address1} onChange={(e) => setF((x) => ({ ...x, address1: e.target.value }))} /></F>
        <div className="grid grid-cols-3 gap-2">
          <F label="City"><Input value={f.city} onChange={(e) => setF((x) => ({ ...x, city: e.target.value }))} /></F>
          <F label="State"><Input maxLength={2} value={f.state} onChange={(e) => setF((x) => ({ ...x, state: e.target.value.toUpperCase() }))} /></F>
          <F label="ZIP"><Input value={f.zip_code} onChange={(e) => setF((x) => ({ ...x, zip_code: e.target.value }))} /></F>
        </div>
      </div>
      <Button onClick={save} disabled={!isSuper} data-testid="payer-save">{isSuper ? 'Save payer' : 'Super Admin required to edit'}</Button>
    </CardContent></Card>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/tax-center/admin/audit?limit=100').then((r) => setRows(r.data.entries || [])).catch(() => {}); }, []);
  return (
    <Card><CardContent className="p-3" data-testid="audit-tab">
      <div className="flex items-center gap-2 mb-2"><ScrollText className="w-4 h-4 text-slate-500" /><h3 className="font-semibold">Tax activity audit</h3></div>
      <div className="space-y-1 text-xs">
        {rows.length === 0 ? <p className="text-slate-500 py-6 text-center">No activity yet.</p> :
          rows.map((e) => (
            <div key={e.id} className="flex items-center justify-between border-b py-1">
              <span><span className="font-medium capitalize">{e.action.replace(/_/g, ' ')}</span> · {e.entity_type || ''}</span>
              <span className="text-slate-500">{e.actor_email || 'system'} · {fmtDate(e.created_at)}</span>
            </div>
          ))}
      </div>
    </CardContent></Card>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>
);
